import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleById, getRelatedArticles } from '@/mocks/articleData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);
  const article = getArticleById(articleId);
  const relatedArticles = getRelatedArticles(articleId);

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到"
          description="红桃影视影视资讯"
          keywords="红桃影视,影视资讯"
          canonicalUrl={`https://hongtaomovies.com/article/${id}`}
        />
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <p className="text-foreground-500">文章未找到</p>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.summary}
        keywords={`红桃影视,${article.category},影视资讯,${article.title}`}
        canonicalUrl={`https://hongtaomovies.com/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        schemaJson={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.summary,
          image: article.image,
          datePublished: article.date,
          author: {
            '@type': 'Organization',
            name: article.author,
          },
          publisher: {
            '@type': 'Organization',
            name: '红桃影视',
          },
        }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <article>
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto py-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: article.category, path: '/' },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto py-8 md:py-12">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">作者：{article.author}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base leading-relaxed">
                {article.summary}
              </p>
            </div>

            <div className="aspect-[2/1] rounded-2xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 红桃影视`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {related.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-2">{related.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}